import React, { useEffect, useState } from 'react';
import Modal from '../UI/Modal';
import Button from '../UI/Button';
import financialService from '../../api/services/financial.service';

interface AddExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  netAccountBalance?: number;
}

interface FormData {
  type: 'profit' | 'expense';
  amount: string;
  paidTo: string;
  description: string;
  periodFromDate: string;
  periodFromTime: string;
  periodToDate: string;
  periodToTime: string;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function nowTime(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const AddExpenseModal: React.FC<AddExpenseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  netAccountBalance = 0,
}) => {
  const [formData, setFormData] = useState<FormData>({
    type: 'expense',
    amount: '',
    paidTo: '',
    description: '',
    periodFromDate: todayIso(),
    periodFromTime: '00:00',
    periodToDate: todayIso(),
    periodToTime: nowTime(),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: 'expense',
        amount: '',
        paidTo: '',
        description: '',
        periodFromDate: todayIso(),
        periodFromTime: '00:00',
        periodToDate: todayIso(),
        periodToTime: nowTime(),
      });
      setError('');
    }
  }, [isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (amount > netAccountBalance) {
      setError(
        `Amount exceeds net account balance (ETB ${netAccountBalance.toLocaleString()}). ` +
          `You cannot add a ${formData.type} greater than the available balance.`
      );
      return;
    }

    if (!formData.paidTo.trim()) {
      setError('Please enter who this is paid to');
      return;
    }

    if (!formData.description.trim()) {
      setError('Please enter a description');
      return;
    }

    if (!formData.periodFromDate || !formData.periodToDate) {
      setError('Please specify the period from and to');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await financialService.create({
        type: formData.type,
        amount,
        paidTo: formData.paidTo.trim(),
        description: formData.description.trim(),
        periodFromDate: formData.periodFromDate,
        periodFromTime: formData.periodFromTime || '00:00',
        periodToDate: formData.periodToDate,
        periodToTime: formData.periodToTime || '23:59',
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || 'Failed to add transaction'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Transaction" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Balance banner */}
        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
          <span className="text-xs font-medium text-blue-700">
            Available Net Account Balance
          </span>
          <span className="text-sm font-bold text-blue-700">
            ETB {netAccountBalance.toLocaleString()}
          </span>
        </div>

        {/* Type */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Transaction Type <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setFormData((p) => ({ ...p, type: 'profit', paidTo: 'shareholders' }));
                setError('');
              }}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                formData.type === 'profit'
                  ? 'bg-green-50 border-green-500 text-green-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Profit
            </button>
            <button
              type="button"
              onClick={() => {
                setFormData((p) => ({ ...p, type: 'expense', paidTo: '' }));
                setError('');
              }}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                formData.type === 'expense'
                  ? 'bg-red-50 border-red-500 text-red-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Expense
            </button>
          </div>
        </div>

        {/* Amount */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Amount (ETB) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleInputChange}
            required
            min="1"
            step="1"
            placeholder="Enter amount"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
          />
        </div>

        {/* Paid To */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Paid To <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="paidTo"
            value={formData.paidTo}
            onChange={handleInputChange}
            required
            placeholder={
              formData.type === 'profit' ? 'shareholders' : 'Enter recipient name'
            }
            disabled={formData.type === 'profit'}
            className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm ${
              formData.type === 'profit' ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
          />
          {formData.type === 'profit' && (
            <p className="text-[10px] text-gray-500 mt-1">
              Profit is always paid to shareholders
            </p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
            rows={2}
            placeholder="Enter description"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm resize-none"
          />
        </div>

        {/* ⭐ Period */}
        <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
          <p className="text-xs font-semibold text-gray-700 mb-2">
            Period covered by this {formData.type}
          </p>

          <div className="space-y-3">
            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-1">
                From <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  name="periodFromDate"
                  value={formData.periodFromDate}
                  onChange={handleInputChange}
                  required
                  className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
                <input
                  type="time"
                  name="periodFromTime"
                  value={formData.periodFromTime}
                  onChange={handleInputChange}
                  required
                  className="w-32 text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-medium text-gray-500 mb-1">
                To <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  name="periodToDate"
                  value={formData.periodToDate}
                  onChange={handleInputChange}
                  required
                  className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
                <input
                  type="time"
                  name="periodToTime"
                  value={formData.periodToTime}
                  onChange={handleInputChange}
                  required
                  className="w-32 text-sm border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-100">
            <p className="text-xs text-red-600">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add Transaction'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddExpenseModal;